/**
 * Section 16 — Risk Management.
 *
 * The discipline section. Daily-loss cap mechanics, kill switch usage,
 * position sizing via per-instrument investment amounts, Reserve %,
 * R-multiple thinking, and the two-week starter discipline.
 */
import React from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_risk_management: GuideSection = {
  id: 'risk-management',
  label: 'Risk Management',
  group: 'setup',
  icon: <ShieldIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Risk Management</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        The discipline layer. Strategy + execution gets you signals; risk management is what keeps you in
        the game long enough for the strategies to compound. Read this <em>before</em> turning Auto Trade ON.
      </Typography>

      <H id="three-layers">The three-layer safety net</H>
      <P>
        earngush has three independent risk controls. Each can stop an order on its own; together they're
        belt + braces + parachute.
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>Per-trade SL</strong> — every position is watched by the app against its stop and closed at market when the stop counts as hit (see <em>The Stop-Loss Plan</em> below). The stop lives in the app, not at the broker, unless you switch on the optional backup stop at Delta.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>Daily-loss cap</strong> — today's realised losses plus live drawdown on open positions. When breached, the order gate blocks new entry orders (exits still fire). A bad day can't become a catastrophic day.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>Order placement kill switch</strong> — global "stop placing new entries" toggle. Manual control for when you want a hard pause without re-doing config. Protective exits are never blocked.</>} />
        </ListItem>
      </List>

      <H id="stop-loss-plan">The Stop-Loss Plan — how a stop is hit</H>
      <P>
        <strong>Settings → Stop-Loss Plan</strong> is one page for every stop rule of futures, perpetual and stock
        positions. At the top, <em>Your plan at a glance</em> describes in steps what will happen to an open position for
        the choices on screen, with a worked example on a price ladder (LONG at 100, stop 95, so R = 5). It updates as you
        change a choice, before you save. Below it, five numbered groups in the order a trade meets them:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>Initial stop and target</strong> — the breach buffer and the target multiple N (alert trades: target = entry ± N × R). A stop or target you pick on the chart is used as it is.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>When does the stop count as hit?</strong> Choose one of two rules, for the initial and the trailed stop alike:
            {' '}<em>Price + hold</em> (default) — price must stay beyond the stop for the hold time (default 2 min; 60 s in spike mode); coming back resets the clock.
            {' '}<em>Candle close</em> — only a candle of the trade's own timeframe (or one you choose: 5m … 1D) closing beyond the stop exits, within about 30 s of the close; wicks are ignored. It always carries an <em>emergency stop</em> 1 R further out (adjustable 0.25–5 R) that closes at once after 10 s beyond it, so a runaway candle is capped at about 2 R plus slippage. On NSE / MCX, a candle that closes after hours is acted on at the next open.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>Trailing</strong> — start at N R of profit (default 2), Heikin-Ashi swings of the trade's timeframe, optional forming candle, and spike mode (profit lock at 2 R / 3 R / 4 R after an exceptional run of at least 5 R).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>Safety net at Delta</strong> — an optional reduce-only stop order resting at Delta at the emergency distance, triggered on mark price, moved with your stop and cancelled when the trade closes. <strong>OFF by default</strong>: the order is visible in Delta's book. With it OFF, nothing exits while the app is down.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<><strong>Monitoring speed</strong> — every position is priced every 30 s; while a stop clock runs, every 1–10 s (FastLane).</>} />
        </ListItem>
      </List>
      <P>
        <strong>One trade, a different rule:</strong> the small chip under each open trade's <em>SL Trigger</em> switch on
        the Dashboard (e.g. <code>2m hold</code> or <code>4H close</code>; a • means this trade overrides your default) opens
        a menu to switch that trade to price + hold, candle close on any timeframe, or back to your default. Changing the
        rule never acts on a candle that closed before the change. <strong>Paper chart trades</strong> follow the same rule
        (target on touch), but are never trailed and never get a backup stop. <strong>Option baskets</strong> have their own
        stops, set on each basket.
      </P>

      <H id="daily-cap">Daily-loss circuit breaker — mechanics</H>
      <P>
        The cap is a rupee amount. Today's "consumed loss" = realised losses on trades closed today (negative
        P&L) <strong>plus</strong> the live unrealised drawdown on currently-open positions — so a single
        large open loser can trip the breaker intraday, not only after it's booked. Wins don't subtract — the
        cap protects against draw-down sequences, not net result.
      </P>
      <P>
        When you place a new entry order, the gate computes consumed-so-far. If consumed ≥ cap, the order is
        rejected with a clear error. Existing positions keep ticking — SLs, TSLs, and Target orders all fire
        regardless of the cap (they're risk-reducing).
      </P>
      <GuideCallout kind="info">
        Two views, one cap: the <strong>enforcement gate</strong> (what blocks new entries) counts today's
        realised loss <em>plus</em> live open drawdown, as above. The on-screen <strong>Risk Cockpit gauge</strong>{' '}
        on the Dashboard tracks <em>today's realised loss</em> vs the cap — so it can read lower than the gate
        when you're holding an open loser. The cap value and on/off state are the same in both.
      </GuideCallout>
      <P>
        Two modes:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>TRACKING</strong> — the cap is shown on the Dashboard but doesn't enforce. Useful when you want awareness but discretion. Toggle <em>enforced</em> OFF in Trading Preferences.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>ENFORCED</strong> — the gate hard-blocks. Recommended.</>} />
        </ListItem>
      </List>
      <GuideCallout kind="info" title="How to size the cap">
        Conservative starting point: 1-2% of total portfolio value per day. ₹50K portfolio → ₹500–1,000
        daily cap. As you build confidence + a track record, you can widen it. Most amateur blow-ups happen
        from "I just need to make back today's losses" — the cap structurally prevents that.
      </GuideCallout>

      <H id="kill-switch">The kill switch — when to use it</H>
      <P>
        The Dashboard's Trading Controls panel has the master <strong>Order Placement</strong> toggle. When
        OFF (= BLOCKED), the order gate rejects every new <strong>entry</strong> order — but protective
        <strong> exits</strong> (stop-loss, target, trailing, manual / square-off closes) still go through, so
        you can always manage open risk. Use cases:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Major news event — Fed announcement, RBI policy, earnings release. Volatility may not respect your stops." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Lunch break — walk away without auto-trade firing into thin liquidity." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Broker maintenance window — orders will fail anyway; better to gate cleanly." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Emotional state — bad sleep, frustrated, distracted. Easier to flip a switch than to talk yourself out of trading." />
        </ListItem>
      </List>
      <GuideCallout kind="info">
        The kill switch only blocks new entries — SL / TSL / target / square-off exits still fire while it's
        OFF, so an open position is never left unmanaged. It is a "stop opening new risk" control, not a
        "freeze everything" control.
      </GuideCallout>

      <H id="position-sizing">Position sizing — per-instrument Investment Amount</H>
      <P>
        On the <OpenInApp to="/instruments" label="Instruments page" />, each instrument has an
        <strong> Investment Amount</strong> field — the default rupee value to deploy per new position on
        that symbol. The OrderConfirmDialog divides this by current price to suggest a quantity.
      </P>
      <P>
        Standard practice: size positions so the worst-case (SL hit) loss is 1× R, where R = a fixed
        fraction of portfolio. Example: portfolio ₹50K, R = 1% = ₹500. For a trade with entry ₹100, SL
        ₹95 (5% stop), max qty = ₹500 / ₹5 = 100 shares. Investment Amount = 100 × ₹100 = ₹10,000.
      </P>
      <P>
        Different SL widths mean different sizes — a tight 0.5% stop allows more shares; a wide 5% stop
        allows fewer. Setting Investment Amount per-instrument gets you in the ballpark; the actual qty
        adjustment happens at OrderConfirm.
      </P>

      <H id="reserve">Margin Reserve %</H>
      <P>
        Setting in Trading Preferences. Says: keep this fraction of total portfolio value as free cash
        even when fully deployed.
      </P>
      <P>
        Why bother? Two reasons. (1) Mid-trade volatility margin spikes can force liquidations if you have
        no buffer. (2) A trading platform should never be 100% deployed — you want dry powder for the
        better setup that shows up tomorrow.
      </P>
      <P>
        Recommended: 20–30% reserve. Dashboard's Available Margin tile flashes red when you're below.
      </P>

      <H id="r-multiple">R-multiple thinking</H>
      <P>
        Stop reading P&L in rupees. Start reading in R-multiples. R = your initial SL risk. A trade that
        risked ₹500 and made ₹1,500 is +3R. A trade that risked ₹2,000 and made ₹1,500 is +0.75R. Both
        made the same rupees; the first was a much better trade.
      </P>
      <P>
        Over many trades, your <strong>expectancy</strong> = average R per trade. Positive expectancy
        with sample size ≥ 50 = you have an edge. Negative = you don't, regardless of the dollar P&L.
      </P>
      <P>
        Trades page → click R column header to sort by R. Top of the list = your best plays; study what
        they had in common. Bottom = your worst; same exercise, what consistently fails.
      </P>

      <H id="two-week-rule">The two-week starter rule</H>
      <P>
        For your first two weeks on earngush — whether you're new to trading or just new to the platform:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Auto Trade OFF. Every order is a manual one-click." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Daily-loss cap enforced at 1% of portfolio. Force the discipline." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="One strategy only — RockBand is the default and the recommended starting point. Multi-strategy noise is hard to interpret in week one." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Small Investment Amount per instrument — 5–10% of what you'd 'normally' size at. The first week is about learning the flow, not maximising P&L." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="End-of-day Trades page review. Sort by R. Note what worked." />
        </ListItem>
      </List>
      <P>
        After two weeks, scale up gradually: bigger Investment Amount, then second strategy, then Auto
        Trade ON for one symbol at a time.
      </P>

      <GuideExample title="A risk-aware day">
        Portfolio ₹50K. Cap ₹500 (1%). Reserve 25%. Auto Trade OFF.<br />
        09:30 — Two Sniper signals fire. Both moderate strength. You take only one (the higher-conviction
        one) — size 50% of normal because you're still learning the strategy.<br />
        10:15 — Trade closes for −₹180 (loss). Daily consumed = ₹180. Cap remaining ₹320.<br />
        11:00 — Another Sniper signal. You take it (smaller). Closes +₹240. Daily consumed = ₹180 (wins
        don't subtract).<br />
        13:00 — Third signal. You're at ₹180 / ₹500 cap. You take it cautiously. Closes −₹220 (loss).
        Daily consumed = ₹400. ₹100 to cap.<br />
        14:30 — Fourth signal. You decline — too close to the cap, risk/reward of one more trade isn't
        worth the rest-of-day blockade. End day +₹60 net, 4 trades, +0.4R avg. Cap intact for tomorrow.
      </GuideExample>

      <H id="circuit-breaker-recovery">After a cap-breach day</H>
      <P>
        Cap breached. New entries blocked. What now?
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Existing positions keep ticking — SL/TSL still fire. The cap only blocks NEW entries." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>Don't reset / raise the cap. The cap exists to stop you in moments like this; "I just need to make it back" is the #1 amateur-trader bankroll killer.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Use the time to review the day in Trades page. What signals failed? Was there a regime change? Did you respect SLs?" />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Cap resets at IST midnight automatically. Tomorrow is a fresh day." />
        </ListItem>
      </List>
    </>
  ),
  searchIndex: [
    { heading: 'The Stop-Loss Plan',      anchor: 'stop-loss-plan', body: 'stop loss plan candle close hold emergency stop backup stop delta trailing spike breach buffer target settings per trade sl mode' },
    { heading: 'Three-layer safety net',  anchor: 'three-layers',   body: 'three layer safety net per trade sl daily loss cap kill switch' },
    { heading: 'Daily-loss cap mechanics', anchor: 'daily-cap',     body: 'daily loss cap mechanics tracking enforced consumed' },
    { heading: 'The kill switch',         anchor: 'kill-switch',    body: 'kill switch order placement when to use news lunch maintenance' },
    { heading: 'Position sizing',         anchor: 'position-sizing',body: 'position sizing investment amount per instrument r risk' },
    { heading: 'Margin Reserve %',        anchor: 'reserve',        body: 'reserve margin percent free cash buffer' },
    { heading: 'R-multiple thinking',     anchor: 'r-multiple',     body: 'r multiple expectancy skill metric sort trades' },
    { heading: 'Two-week starter rule',   anchor: 'two-week-rule',  body: 'two week starter rule discipline manual auto trade off' },
    { heading: 'After cap-breach day',    anchor: 'circuit-breaker-recovery', body: 'cap breach recovery discipline reset reset midnight' },
  ],
};

export default Section_risk_management;
